import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Package, Clock, Check, XCircle } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Orders = () => {
  const { user, loading: authLoading } = useAuth();
  const { t } = useLanguage();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchOrders = async () => {
      if (!user) return;
      
      try {
        const response = await axios.get(`${API}/orders`);
        setOrders(response.data);
      } catch (error) {
        console.error('Failed to fetch orders:', error);
      } finally {
        setLoading(false);
      }
    };

    if (!authLoading) {
      fetchOrders();
    }
  }, [user, authLoading]);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('pl-PL', { style: 'currency', currency: 'PLN' }).format(price);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('pl-PL', {
      year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return <Clock size={16} className="text-yellow-500" />;
      case 'completed': return <Check size={16} className="text-green-500" />;
      case 'cancelled': return <XCircle size={16} className="text-red-500" />;
      default: return <Clock size={16} className="text-zinc-500" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pending': return t.orders.pending;
      case 'completed': return t.orders.completed;
      case 'cancelled': return t.orders.cancelled;
      default: return status;
    }
  };

  if (!user && !authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-heading text-3xl uppercase text-white mb-4">{t.auth.login}</h1>
          <p className="text-zinc-400 mb-6">{t.cart.loginToCheckout}</p>
          <Link to="/login" className="bg-yellow-400 text-black uppercase font-bold tracking-widest px-8 py-4 border-2 border-yellow-400 hover:bg-black hover:text-yellow-400 transition-colors inline-block">
            {t.auth.login}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="orders-page">
      <div className="bg-[#111111] border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
          <span className="font-mono text-xs tracking-[0.2em] uppercase text-yellow-500 mb-2 block">{t.orders.account}</span>
          <h1 className="font-heading text-4xl sm:text-5xl tracking-tighter uppercase leading-none text-white" data-testid="orders-title">{t.orders.title}</h1>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="border border-zinc-800 p-6 animate-pulse">
                <div className="h-6 bg-zinc-800 w-1/4 mb-4" />
                <div className="h-4 bg-zinc-800 w-1/2" />
              </div>
            ))}
          </div>
        ) : orders.length === 0 ? (
          <div className="text-center py-16 border border-zinc-800">
            <Package size={64} className="mx-auto text-zinc-700 mb-6" />
            <h2 className="font-heading text-2xl uppercase text-white mb-4">{t.orders.noOrders}</h2>
            <p className="text-zinc-400 mb-8">{t.orders.noOrdersDesc}</p>
            <Link to="/shop" className="bg-yellow-400 text-black uppercase font-bold tracking-widest px-8 py-4 border-2 border-yellow-400 hover:bg-black hover:text-yellow-400 transition-colors inline-block" data-testid="start-shopping-btn">
              {t.orders.startShopping}
            </Link>
          </div>
        ) : (
          <div className="space-y-6">
            {orders.map((order) => (
              <div key={order.id} className="border border-zinc-800 bg-[#111111]" data-testid={`order-${order.id}`}>
                <div className="p-4 md:p-6 border-b border-zinc-800 flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div>
                    <span className="font-mono text-xs tracking-widest uppercase text-zinc-500 block mb-1">{t.orders.orderId}</span>
                    <span className="font-mono text-sm text-yellow-400">{order.id.slice(0, 8)}...</span>
                  </div>
                  <div>
                    <span className="font-mono text-xs tracking-widest uppercase text-zinc-500 block mb-1">{t.orders.date}</span>
                    <span className="text-zinc-300 text-sm">{formatDate(order.created_at)}</span>
                  </div>
                  <div>
                    <span className="font-mono text-xs tracking-widest uppercase text-zinc-500 block mb-1">{t.orders.status}</span>
                    <span className="flex items-center gap-2 text-sm capitalize">
                      {getStatusIcon(order.status)}
                      {getStatusText(order.status)}
                    </span>
                  </div>
                  <div>
                    <span className="font-mono text-xs tracking-widest uppercase text-zinc-500 block mb-1">{t.orders.total}</span>
                    <span className="font-mono text-xl text-yellow-500">{formatPrice(order.total)}</span>
                  </div>
                </div>

                <div className="p-4 md:p-6">
                  <div className="space-y-4">
                    {order.items.map((item, index) => (
                      <div key={index} className="flex items-center gap-4">
                        <div className="w-16 h-16 bg-zinc-800 flex-shrink-0 flex items-center justify-center">
                          <Package size={24} className="text-zinc-600" />
                        </div>
                        <div className="flex-1">
                          <h4 className="font-bold text-white">{item.name}</h4>
                          <p className="text-zinc-500 text-sm">{t.common.qty}: {item.quantity}</p>
                        </div>
                        <span className="font-mono text-yellow-500">{formatPrice(item.price * item.quantity)}</span>
                      </div>
                    ))}
                  </div>

                  <div className="mt-6 pt-6 border-t border-zinc-800">
                    <span className="font-mono text-xs tracking-widest uppercase text-zinc-500 block mb-2">{t.orders.shippingAddress}</span>
                    <p className="text-zinc-300 text-sm">
                      {order.shipping_info.full_name}<br />
                      {order.shipping_info.address}<br />
                      {order.shipping_info.postal_code} {order.shipping_info.city}<br />
                      Tel: {order.shipping_info.phone}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Orders;
